import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useLoginUserData } from "../Utility";
import { onLoginUser, onLoginUserReset } from "../Store/Slices/loginSlice";
import {
  setBusinessOnboardingComplete,
  setIsLoggedIn,
} from "../Store/Slices/utilitySlice";
import { setAccessToken, setAdminAuth } from "./auth";

/**
 * Shared validation for login form (email + password).
 * Returns true if valid; sets errors via setErrors and returns false otherwise.
 */
export function validateLoginForm({ email, password }, setErrors) {
  const newErrors = {};
  if (!email.trim()) {
    newErrors.email = "Email is required";
  } else if (!/\S+@\S+\.\S+/.test(email)) {
    newErrors.email = "Email is invalid";
  }
  if (!password) {
    newErrors.password = "Password is required";
  }
  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
}

/**
 * Shared login form hook. Same API flow for admin and enduser; behavior differs by userType.
 * - admin: on success → setAdminAuth(), reset, navigate to /admin/dashboard (no modal).
 * - user | business: on success → setAccessToken, setIsLoggedIn, set isSuccess (enduser shows modal, then handleSuccessComplete navigates).
 */
export function useLoginForm(userType) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const loginUserData = useLoginUserData();

  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSuccess, setIsSuccess] = useState(false);

  const isAdmin = userType === "admin";

  useEffect(() => {
    const status = loginUserData?.status_code;
    const ok = status === 200 || status === "200";

    if (ok) {
      const loginData = loginUserData?.loginData ?? loginUserData?.data;
      const token =
        loginData?.token ??
        loginData?.accessToken ??
        loginData?.data?.token ??
        loginData?.data?.accessToken;

      if (isAdmin) {
        if (token) setAccessToken(token);
        setAdminAuth();
        dispatch(onLoginUserReset());
        navigate("/admin/dashboard", { replace: true });
        return;
      }

      if (token) setAccessToken(token);
      dispatch(setIsLoggedIn(true));
      setIsSuccess(true);
      return;
    }


    const resetState = (status == null || status === 0) && !loginUserData?.isLoading;
    if (resetState) {
      setErrors({});
      return;
    }

    if (status != null && !ok) {
      
      setErrors({
        password: loginUserData?.message,
      });
    }
  }, [
    loginUserData?.status_code,
    loginUserData?.message,
    loginUserData?.loginData,
    loginUserData?.data,
    loginUserData?.isLoading,
    isAdmin,
    dispatch,
    navigate,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () =>
    validateLoginForm(
      { email: formData.email, password: formData.password },
      setErrors,
    );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      dispatch(
        onLoginUser({
          email: formData.email,
          password: formData.password,
          user_type: userType,
        }),
      );
    }
  };

  const handleSuccessComplete = () => {
    const loginData = loginUserData?.loginData ?? loginUserData?.data;
    const token =
      loginData?.token ??
      loginData?.accessToken ??
      loginData?.data?.token ??
      loginData?.data?.accessToken;
    if (token) setAccessToken(token);

    dispatch(onLoginUserReset());
    setIsSuccess(false);
    if (loginData?.user_type === "business") {
      const profileComplete =
        loginData?.status === "profile_completed" ||
        loginData?.status === "approved";
      
      if (profileComplete) {
        dispatch(setBusinessOnboardingComplete(true));
      }
      loginData?.status !== "approved"
        ? navigate("/profile/business", { replace: true })
        : navigate("/", { replace: true });
    } else {
      navigate("/", { replace: true });
    }
  };

  return {
    formData,
    setFormData,
    showPassword,
    setShowPassword,
    errors,
    setErrors,
    handleChange,
    validateForm,
    handleSubmit,
    handleSuccessComplete,
    isLoading: loginUserData?.isLoading ?? false,
    isSuccess,
    setIsSuccess,
    loginUserData,
  };
}
